import React, { useCallback, useMemo, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import {
  CheckCircle,
  Circle,
  ListChecks,
  Minus,
  Target as TargetIcon,
  TrendDown,
  TrendUp,
} from "@phosphor-icons/react";

import type {
  SessionMetricPoint,
  StepAcquisitionSequencePoint,
  TargetTrendSummary,
  TargetVisualAnalysis,
  TrendClassification,
} from "api/types/charts";
import type { TargetMarker } from "api/types/markers";
import type { ToggleOption } from "../../shared/ToggleChartGroup";
import { ChartEmptyState, ChartErrorState } from "../../shared/ChartStateCard";
import TargetMetricChart from "../TargetMetricChart";
import type { XAxisMode } from "../TargetChart";
import TargetInsightCard, { type InsightStat, type InsightTone } from "../TargetInsightCard";
import VisualAnalysisSummaryCard, {
  type VisualAnalysisStat,
} from "../VisualAnalysisSummaryCard";
import TargetDistributionChart, { type DistributionDatum } from "../TargetDistributionChart";
import TargetStepBreakdownChart, { type StepBreakdownRow } from "../TargetStepBreakdownChart";
import { getMetricChartConfig, hasLineMetricValue } from "../metricChartRegistry";
import { buildDistributionData } from "../../utils/buildDistributionData";
import { buildStepAcquisitionRows } from "../../utils/buildStepAcquisitionRows";
import {
  deriveChainCompletion,
  deriveMasteryStatus,
  toBannerCriteria,
  type MasteryStatusLevel,
} from "../../utils/deriveStatusInsights";
import { derivePhaseZones } from "../../utils/derivePhaseZones";
import { resolveChartMarkers } from "../../utils/resolveChartMarkers";
import {
  directionsForTarget,
  type MasteryBannerPhase,
} from "../../utils/evaluateMasteryBanner";

// ---------------------------------------------------------------------------
// Presentation helpers for the server-computed trend / visual-analysis
// insights (`TargetTrendSummary` / `TargetVisualAnalysis`) — the backend
// returns raw numbers/classifications; formatting into card props stays here.
// ---------------------------------------------------------------------------

const round1 = (n: number): string => (Math.round(n * 10) / 10).toFixed(1);
const round2 = (n: number): string => (Math.round(n * 100) / 100).toFixed(2);

const TREND_CLASSIFICATION_TONE: Record<TrendClassification, InsightTone> = {
  improving: "success",
  declining: "error",
  stable: "neutral",
};

const TREND_CLASSIFICATION_TITLE: Record<TrendClassification, string> = {
  improving: "Improving",
  declining: "Declining",
  stable: "Stable",
};

function trendInsightCardContent(trend: TargetTrendSummary): {
  tone: InsightTone;
  title: string;
  subtitle: string;
  stats: InsightStat[];
} {
  const magnitude = round1(Math.abs(trend.raw_delta));
  const changeWord = trend.raw_delta >= 0 ? "increase" : "decrease";

  return {
    tone: TREND_CLASSIFICATION_TONE[trend.classification],
    title: TREND_CLASSIFICATION_TITLE[trend.classification],
    subtitle: `${magnitude} point ${changeWord} over last ${trend.window} sessions`,
    stats: [
      { label: "Early Mean", value: round1(trend.early_mean) },
      { label: "Recent Mean", value: round1(trend.recent_mean) },
    ],
  };
}

function visualAnalysisStats(analysis: TargetVisualAnalysis): VisualAnalysisStat[] {
  const trendLabel =
    analysis.trend_classification === "accelerating"
      ? "Accelerating"
      : analysis.trend_classification === "decelerating"
        ? "Decelerating"
        : "Stable";
  const variabilityLabel = analysis.variability_classification === "high" ? "High" : "Low";

  return [
    {
      label: "Level",
      value: round1(analysis.level_mean),
      legend: "mean",
      detail: `${round1(analysis.level_median)} median`,
    },
    {
      label: "Trend",
      value: round2(analysis.trend_slope),
      legend: "slope/session",
      detail: trendLabel,
    },
    {
      label: "Variability",
      value: `${round1(analysis.variability_cv_percent)}%`,
      legend: "CV",
      detail: variabilityLabel,
    },
  ];
}

// Trend-direction icon per classification, shown in the trend insight card.
const TREND_ICON: Record<TrendClassification, React.ReactNode> = {
  improving: <TrendUp size={24} />,
  declining: <TrendDown size={24} />,
  stable: <Minus size={24} />,
};

// Mastery-status icon per level, shown in the mastery status card.
const MASTERY_STATUS_ICON: Record<MasteryStatusLevel, React.ReactNode> = {
  mastered: <CheckCircle size={24} weight="fill" />,
  approaching: <TargetIcon size={24} />,
  "in-progress": <Circle size={24} />,
};

// Phase label for the Trials/Opportunities to Criterion card's stat row.
const PHASE_LABEL: Record<MasteryBannerPhase, string> = {
  baseline: "Baseline",
  intervention: "Intervention",
  generalization: "Generalization",
  maintenance: "Maintenance",
};

const KNOWN_SEGMENT_IDS = new Set([
  "trend",
  "trend-level-variability",
  "mastery-status",
  "chain-completion",
  "trials-to-criterion",
  "prompt-level-distribution",
  "step-level-errors",
  "step-acquisition-sequence",
  "rating-distribution",
  "latency-by-prompt-level",
]);

export interface TargetChartAreaProps {
  /** The active toggle option — its `id` routes segment cards, `label` line charts. */
  activeOption: ToggleOption | undefined;
  /** The target's session metrics (oldest first). */
  sessionMetrics: SessionMetricPoint[];
  /** Whether the session metrics query is loading. */
  isLoading: boolean;
  /** Truthy when the session metrics query errored. */
  error: unknown;
  /** Retry handler for the error state. */
  onRetry: () => void;
  /** Target type — drives mastery/regression comparison direction. */
  targetType: string | null | undefined;
  /** Target's L1 behavioral dimension — also drives mastery/regression comparison direction. */
  l1Dimension?: string | null;
  /** Target clinical phase — tweaks mastery-status copy (generalization). */
  phase?: MasteryBannerPhase;
  /** Target's stored mastery criteria JSON. */
  masteryCriteria: unknown;
  /** Server-computed trend classification for the target's primary metric. */
  trend?: TargetTrendSummary | null;
  /** Server-computed level/trend/variability trifecta (free-operant targets). */
  visualAnalysis?: TargetVisualAnalysis | null;
  /** Server-computed phase-bounded "Trials/Opportunities to Criterion" aggregate. */
  trialsToCriterion?: number | null;
  /** Opens the Add Marker dialog from the line chart. */
  onAddMarker: () => void;
  /** Opens the edit flow for a marker picked on the line chart. */
  onEditMarker?: (markerId: number) => void;
  /** Opens the delete flow for a marker picked on the line chart. */
  onDeleteMarker?: (markerId: number) => void;
  /** Task-analysis step acquisition sequence derived server-side when available. */
  stepAcquisitionSequence?: StepAcquisitionSequencePoint[];
  /** Persisted chart markers for the target, drawn as vertical lines on line charts. */
  markers?: TargetMarker[];
  /**
   * Fires with the `target_session_id` of the chart point under the pointer
   * (or `null` on leave), so the parent can highlight the matching session row.
   */
  onHoverSessionChange?: (sessionId: string | null) => void;
}

/**
 * Chart-area dispatcher for the target detail view. Given the active metric
 * toggle, it renders exactly one of: a loading spinner, the error state, one of
 * the derived insight/summary cards (trend, mastery status, trials to criterion,
 * visual analysis), the session line chart, or the empty state.
 *
 * All the per-segment derivation + presentation lives here so the page-level
 * `TargetDetail` stays a layout/orchestration component: adding a new metric
 * card only touches this file.
 *
 * @param props - See {@link TargetChartAreaProps}.
 */
const TargetChartArea: React.FC<TargetChartAreaProps> = ({
  activeOption,
  sessionMetrics,
  isLoading,
  error,
  onRetry,
  targetType,
  l1Dimension,
  phase,
  masteryCriteria,
  trend,
  visualAnalysis,
  trialsToCriterion,
  onAddMarker,
  onEditMarker,
  onDeleteMarker,
  stepAcquisitionSequence,
  markers,
  onHoverSessionChange,
}) => {
  // Trend line overlay toggle for the line chart.
  const [showTrendLine, setShowTrendLine] = useState(false);

  // X-axis display mode toggle. Defaults to dates — some payors require the
  // chart axis to show session dates rather than session numbers.
  const [xAxisMode, setXAxisMode] = useState<XAxisMode>("date");

  // Chart config for the active metric — null for card/empty segments.
  const activeChartConfig = useMemo(
    () => getMetricChartConfig(activeOption?.label),
    [activeOption]
  );

  // Omit sessions missing a value for the active line metric so charts do not
  // render a visible gap/tick for empty target_session rows.
  const lineChartMetrics = useMemo(() => {
    if (!activeChartConfig || activeChartConfig.kind === "distribution") {
      return sessionMetrics;
    }
    return sessionMetrics.filter((metric) => hasLineMetricValue(metric, activeChartConfig));
  }, [activeChartConfig, sessionMetrics]);

  // Bridges a hovered chart index to the session row highlight. The chart's
  // data array is parallel to `lineChartMetrics` (filtered series), so the
  // index maps straight to a `target_session_id`.
  const handleActivePointChange = useCallback(
    (index: number | null) => {
      if (!onHoverSessionChange) return;
      const id = index != null ? lineChartMetrics[index]?.target_session_id : undefined;
      onHoverSessionChange(id != null ? String(id) : null);
    },
    [onHoverSessionChange, lineChartMetrics]
  );

  // Mastery Status segment — reuses the mastery evaluator (mastery side only).
  const masteryStatus = useMemo(() => {
    if (activeOption?.id !== "mastery-status") return null;
    const { masteryDirection } = directionsForTarget({ l1Dimension, targetType });
    return deriveMasteryStatus({
      metrics: sessionMetrics,
      masteryCriteria: toBannerCriteria(masteryCriteria, l1Dimension),
      direction: masteryDirection,
      phase,
    });
  }, [activeOption?.id, sessionMetrics, masteryCriteria, l1Dimension, targetType, phase]);

  // Trend / Level / Variability segment — free-operant visual-analysis trifecta,
  // server-computed (`visualAnalysis` prop) — only shown for its matching segment.
  const visualAnalysisCard = activeOption?.id === "trend-level-variability" ? visualAnalysis : null;

  // Trend segment ("Trend Over Time", "Duration Trend", …) — server-computed
  // (`trend` prop) — only shown for its matching segment.
  const trendCard = activeOption?.id === "trend" ? trend : null;

  // Trials/Opportunities to Criterion segment — server-computed phase-bounded
  // aggregate trial count; the label ("Trials" vs "Opportunities") comes from
  // the active toggle option.
  const trialsToCriterionValue =
    activeOption?.id === "trials-to-criterion" ? trialsToCriterion : null;

  // Chain Completion Rate segment — task-analysis, latest rate + average/best.
  const chainCompletion = useMemo(() => {
    if (activeOption?.id !== "chain-completion") return null;
    return deriveChainCompletion({ metrics: sessionMetrics });
  }, [activeOption?.id, sessionMetrics]);

  const distributionData = useMemo<DistributionDatum[] | null>(
    () => buildDistributionData(activeOption?.id, sessionMetrics),
    [activeOption?.id, sessionMetrics]
  );

  const stepAcquisitionRows = useMemo<StepBreakdownRow[] | null>(() => {
    if (activeOption?.id !== "step-acquisition-sequence") return null;
    return buildStepAcquisitionRows(stepAcquisitionSequence, Math.max(1, sessionMetrics.length));
  }, [activeOption?.id, stepAcquisitionSequence, sessionMetrics.length]);

  // Clinical phase background bands, derived from each point's `phase`.
  const phaseZones = useMemo(() => derivePhaseZones(lineChartMetrics), [lineChartMetrics]);

  // Marker annotation lines, resolved from session ids to chart x-positions.
  const markerLines = useMemo(
    () => resolveChartMarkers(markers ?? [], lineChartMetrics),
    [markers, lineChartMetrics]
  );

  // Mastery criterion line — only a percent threshold may be plotted. Gating on
  // the *chart* being a percentage chart is not enough: duration/latency targets
  // own a real percentage chart ("% Trials Meeting Duration Criterion") while
  // their criterion is in seconds, which would draw a 26-second criterion at
  // y=26 on a 0–100% axis. Gate on the threshold's own unit instead.
  const masteryCriterion = useMemo(() => {
    const criteria = toBannerCriteria(masteryCriteria, l1Dimension);
    return criteria?.threshold_unit === "percent" ? criteria.threshold : undefined;
  }, [masteryCriteria, l1Dimension]);

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={320}>
        <CircularProgress aria-label="Loading session metrics" />
      </Box>
    );
  }

  if (error) return <ChartErrorState onRetry={onRetry} minHeight={320} />;

  if (trendCard) {
    return (
      <TargetInsightCard
        icon={TREND_ICON[trendCard.classification]}
        {...trendInsightCardContent(trendCard)}
      />
    );
  }

  if (masteryStatus) {
    return (
      <TargetInsightCard
        layout="column"
        icon={MASTERY_STATUS_ICON[masteryStatus.status]}
        tone={masteryStatus.tone}
        title={masteryStatus.title}
        subtitle={masteryStatus.subtitle}
        stats={masteryStatus.stats}
      />
    );
  }

  if (visualAnalysisCard) {
    return <VisualAnalysisSummaryCard stats={visualAnalysisStats(visualAnalysisCard)} />;
  }

  if (trialsToCriterionValue != null) {
    const noun = activeOption?.label === "Opportunities to Criterion" ? "opportunities" : "trials";
    return (
      <TargetInsightCard
        layout="column"
        icon={<TargetIcon size={24} />}
        tone="accent"
        title={String(trialsToCriterionValue)}
        subtitle={`${noun} recorded in Baseline/Intervention`}
        stats={[
          { label: "Total Sessions", value: String(sessionMetrics.length) },
          { label: "Current Phase", value: phase ? PHASE_LABEL[phase] : "—" },
        ]}
      />
    );
  }

  if (chainCompletion) {
    return (
      <TargetInsightCard
        layout="column"
        icon={<ListChecks size={24} />}
        tone={chainCompletion.tone}
        title={chainCompletion.title}
        subtitle={chainCompletion.subtitle}
        stats={chainCompletion.stats}
      />
    );
  }

  if (distributionData && activeOption) {
    const xAxisLabel =
      activeOption.id === "step-level-errors"
        ? "Error Count"
        : activeOption.id === "latency-by-prompt-level"
          ? "Latency (s)"
          : "Count";

    return (
      <TargetDistributionChart
        title={activeOption.label}
        data={distributionData}
        xAxisLabel={xAxisLabel}
        onAddMarker={onAddMarker}
      />
    );
  }

  if (stepAcquisitionRows) {
    return (
      <TargetStepBreakdownChart
        title={activeOption?.label ?? "Step Acquisition Sequence"}
        rows={stepAcquisitionRows}
        totalSessions={Math.max(1, sessionMetrics.length)}
        onAddMarker={onAddMarker}
      />
    );
  }

  if (activeOption?.id && !KNOWN_SEGMENT_IDS.has(activeOption.id) && !activeChartConfig) {
    return (
      <ChartEmptyState
        title="Unsupported chart segment."
        description="This metric segment is not available in this build."
        minHeight={320}
      />
    );
  }

  if (
    activeChartConfig &&
    activeChartConfig.kind !== "distribution" &&
    lineChartMetrics.length > 0
  ) {
    return (
      <TargetMetricChart
        config={activeChartConfig}
        metrics={lineChartMetrics}
        masteryCriterion={masteryCriterion}
        phaseZones={phaseZones}
        markers={markerLines}
        xAxisMode={xAxisMode}
        onToggleXAxisMode={setXAxisMode}
        showTrendLine={showTrendLine}
        onToggleTrendLine={setShowTrendLine}
        onAddMarker={onAddMarker}
        onEditMarker={onEditMarker}
        onDeleteMarker={onDeleteMarker}
        onActivePointChange={handleActivePointChange}
      />
    );
  }

  return <ChartEmptyState minHeight={320} />;
};

export default TargetChartArea;
